import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from 'react';
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import PlusIcon from 'mdi-react/PlusIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import LockOutlineIcon from 'mdi-react/LockOutlineIcon';
import AccountOutlineIcon from 'mdi-react/AccountOutlineIcon';
import EmailOutlineIcon from 'mdi-react/EmailOutlineIcon';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { toast } from 'react-toastify';
import QuestionModal from './QuestionModal';

import { EventContext } from '../context';
import Api from '../../../util/api';
import useConfig from '../../../util/useConfig';
import LibraryQuestionItem from './QuestionnaireBuilder/LibraryQuestionItem';
import PickedQuestionItem from './QuestionnaireBuilder/PickedQuestionItem';
import FormCard from '../../../shared/components/custom/FormCard';
import InfoBox from '../../../shared/components/custom/InfoBox';
import DeleteModal from '../../../shared/components/custom/DeleteModal';
import useModal from '../../../shared/hooks/useModal';
import { partial } from '../../../util/translation/translation';
import AlertModal, {
  AlertModalType,
} from '../../../shared/components/custom/AlertModal';
import QuestionsStatsCard from './QuestionsStatsCard';
import Loading from '../../shared/Spinner';

const DEFAULT_GUEST_FIELDS = { name: 'off', email: 'off' };
const GUEST_FIELD_SETTINGS = ['off', 'optional', 'required'];

// Library type filters; each groups the question types it shows
const LIBRARY_FILTERS = {
  all: null,
  text: ['text_field', 'image_upload'],
  choice: ['dropdown', 'checkbox', 'radio_button'],
  reservation: ['reservation'],
  fan: ['score_predictor', 'player_picker'],
};

// What a save would send, to tell whether anything changed since
const formSnapshot = (questions, guestFields) =>
  JSON.stringify({ questions, guestFields });

// onSaved: called after the form is saved or cleared (a series copies its
// questions to its occurrences then)
const QuestionCard = ({ eventId, onSaved }) => {
  const [tab, setTab] = useState('1');
  const [loading, setLoading] = useState(true);
  const [isNewForm, setIsNewForm] = useState(true);
  const [questionList, setQuestionList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [guestFields, setGuestFields] = useState(DEFAULT_GUEST_FIELDS);
  // Saved before guest fields existed: the app still asks every guest for
  // an optional name and details until the form is saved here
  const [legacyGuests, setLegacyGuests] = useState(false);
  // The event has saved questions/guest fields, even if no questions
  const [hasSavedForm, setHasSavedForm] = useState(false);
  const {
    questions,
    setPickedQuestions,
    pickedQuestions,
    setEventId,
    fetchQuestions,
    setPickedQuestionId,
  } = useContext(EventContext);
  const [search, setSearch] = useState('');
  const [libraryFilter, setLibraryFilter] = useState('all');
  const savedSnapshot = useRef(null);
  const { id } = useConfig();
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editability, setEditability] = useState([]);
  const [handlePickedQuestionModal, pickedVisible, deletedPickedQuestion] =
    useModal();
  const [handleLibraryQuestionModal, libraryVisible, deletedLibraryQuestion] =
    useModal();
  const [handleClearAllQuestionModal, clearQuestionVisible] = useModal();
  const qT = partial('question');

  const getDomainQuestions = useCallback(async () => {
    const { data } = await Api.questions.fetchDomainSpecificQuestions(
      eventId,
      id
    );
    setGuestFields(data?.guestFields || DEFAULT_GUEST_FIELDS);
    setLegacyGuests(!!data?.id && !data.guestFields);
    setHasSavedForm(!!data?.id);
    if (data?.id) {
      // Also when the saved form has no questions (e.g. guest fields only)
      setPickedQuestionId(data.id);
      setIsNewForm(false);
    }
    savedSnapshot.current = formSnapshot(
      data?.question?.length > 0 ? data.question : [],
      data?.guestFields || DEFAULT_GUEST_FIELDS
    );
    if (data?.question?.length > 0) {
      setPickedQuestions(data.question);
      if (
        Array.isArray(data.question) &&
        data.question?.length &&
        !!isNewForm
      ) {
        setIsNewForm(false);
      }
    } else {
      setPickedQuestions([]);
    }
    setLoading(false);
  }, []);

  const searchSharedQuestions = useCallback(async () => {
    const { data } = await Api.questions.searchSharedQuestions(
      { domain: 'event', query: search },
      id
    );
    setQuestionList(data);
  }, [search]);

  useEffect(() => {
    setEventId(eventId);
    getDomainQuestions();
  }, []);

  useEffect(() => {
    setQuestionList(questions);
  }, [questions]);

  useEffect(() => {
    const timer = setTimeout(() => {
      searchSharedQuestions();
    }, 200);
    return () => clearTimeout(timer);
  }, [search]);

  // The library as shown: drag indexes refer to this filtered list
  const visibleLibrary = LIBRARY_FILTERS[libraryFilter]
    ? questionList.filter((q) =>
        LIBRARY_FILTERS[libraryFilter].includes(q.question?.question_type)
      )
    : questionList;
  const isPicked = (questionId) =>
    pickedQuestions.some((picked) => picked.id === questionId);

  const checkIfExists = (source) =>
    source.droppableId === 'list' && isPicked(visibleLibrary[source.index]?.id);

  const handleOnDrag = ({ source, destination }) => {
    const existingPicked = [...pickedQuestions];
    const existingList = [...questionList];

    let sourceQuestion = {
      id: visibleLibrary[source.index]?.id,
      ...visibleLibrary[source.index]?.question,
    };

    if (!destination) return;
    if (source.droppableId === 'pick' && destination.droppableId === 'list') {
      return;
    }
    if (
      source.droppableId === destination.droppableId &&
      source.droppableId === 'list'
    ) {
      return;
    }
    if (checkIfExists(source)) return;

    if (source.droppableId === 'pick') {
      sourceQuestion = existingPicked[source.index];
    }

    if (source.droppableId === destination.droppableId) {
      // Move, not swap, so the result matches what the drag showed
      if (source.index === destination.index) return;
      const [moved] = existingPicked.splice(source.index, 1);
      existingPicked.splice(destination.index, 0, moved);
    } else {
      existingPicked.splice(destination.index, 0, sourceQuestion);
    }
    setEditability((prevEditability) => [
      ...prevEditability,
      sourceQuestion.id,
    ]);
    setPickedQuestions(existingPicked);
    setQuestionList(existingList);
  };

  // The Add button: same as dropping the question at the end of the form
  const addFromLibrary = (libraryQuestion) => {
    if (isPicked(libraryQuestion.id)) return;
    setEditability((prevEditability) => [
      ...prevEditability,
      libraryQuestion.id,
    ]);
    setPickedQuestions([
      ...pickedQuestions,
      { id: libraryQuestion.id, ...libraryQuestion.question },
    ]);
  };

  const handleAddQuestions = async () => {
    try {
      const { data: savedForm } = await Api.questions.createDomainQuestions(
        {
          domainId: eventId,
          // Reservations are always the participant's
          question: pickedQuestions.map(({ askFor, ...question }) =>
            question.question_type === 'reservation' || !askFor
              ? question
              : { ...question, askFor }
          ),
          guestFields,
        },
        id
      );
      const toastMessage = isNewForm
        ? qT('questionsAdded')
        : qT('questionsUpdated');
      toast.success(toastMessage);
      // Editing a question saves it to this form, so it needs the form's id
      // (unknown until the first save on an event without questions)
      if (savedForm?.id) setPickedQuestionId(savedForm.id);
      savedSnapshot.current = formSnapshot(pickedQuestions, guestFields);
      setEditability([]);
      setIsNewForm(false);
      setLegacyGuests(false);
      setHasSavedForm(true);
      onSaved?.();
    } catch (error) {
      toast.error(error);
    }
  };
  const handleClearQuestions = async () => {
    try {
      await Api.questions.deleteAllQuestionsInDomain(eventId);
      toast.success(qT('questionsCleared'));
      setEditability([]);
      setIsNewForm(false);
      getDomainQuestions(eventId, id);
      handleClearAllQuestionModal();
      onSaved?.();
    } catch (error) {
      toast.error(error);
    }
  };

  const handleSearch = async (e) => {
    setSearch(e.target.value);
  };

  const handlePickedQuestionDelete = async () => {
    const q = deletedPickedQuestion;
    const selectedQuestions = [...pickedQuestions];
    const index = selectedQuestions.findIndex(
      (question) => question.id === q.id
    );
    if (index !== -1) {
      selectedQuestions.splice(index, 1);
      try {
        await Api.questions.deleteDomainQuestion(eventId, {
          question: selectedQuestions,
        });
        getDomainQuestions(eventId, id);
        onSaved?.();
      } catch (error) {
        toast.error(error);
      }
    }
    handlePickedQuestionModal();
  };

  const handlePickedQuestionEdit = (question) => {
    setEditingQuestion({
      id: question.id,
      question,
      type: 'domain',
    });
    setShowModal(true);
    setEditability([]);
  };

  const handlePromptClose = () => {
    setShowModal(false);
    setEditingQuestion(null);
  };

  const handleLibraryEdit = async (q) => {
    setEditingQuestion(q);
    setShowModal(true);
  };

  const addNewQuestion = () => {
    setShowModal(true);
  };

  const handleLibraryDelete = async () => {
    const question = deletedLibraryQuestion;
    try {
      await Api.questions.deleteSharedQuestion(question.id);
      toast.success(qT('questionDeleted'));
      fetchQuestions(true);
    } catch (error) {
      toast.error(error);
    }
    handleLibraryQuestionModal();
  };

  const movePickedQuestions = (question, direction) => {
    setPickedQuestions((existing) => {
      const currentIndex = existing.findIndex((q) => q.id === question.id);
      const desiredIndex = currentIndex + direction;
      existing.splice(currentIndex, 1);
      existing.splice(desiredIndex, 0, question);
      return [...existing];
    });
  };

  const handlePickedQuestionUp = (question) => {
    movePickedQuestions(question, -1);
  };

  const handlePickedQuestionDown = (question) => {
    movePickedQuestions(question, 1);
  };

  const handleDomainQuestionUpdate = (question) => {
    movePickedQuestions(question, 0);
  };

  const formButtonLabel = isNewForm ? qT('create') : qT('update');
  const asksGuests = guestFields.name !== 'off' || guestFields.email !== 'off';
  // A saved form keeps its buttons so guest fields can be turned off again
  const showFormButtons =
    pickedQuestions.length > 0 || asksGuests || hasSavedForm;

  const guestFieldStateText = {
    off: qT('guestFieldStateOff'),
    optional: qT('guestFieldStateOptional'),
    required: qT('guestFieldStateRequired'),
  };

  const guestFieldOptionLabel = {
    off: qT('guestFieldOff'),
    optional: qT('guestFieldOptional'),
    required: qT('guestFieldRequired'),
  };

  const hasUnsavedChanges =
    savedSnapshot.current !== null &&
    savedSnapshot.current !== formSnapshot(pickedQuestions, guestFields);

  const filterLabel = {
    all: qT('filterAll'),
    text: qT('filterText'),
    choice: qT('filterChoice'),
    reservation: qT('reservation'),
    fan: qT('filterFan'),
  };

  // One row per guest field: its name and what the app does with it on
  // the left, Off / Optional / Required on the right
  const renderGuestFieldSelect = (field, label, Icon) => (
    <div className="qb-guest-field">
      <span className="qb-guest-field__icon" aria-hidden>
        <Icon size={18} />
      </span>
      <div className="qb-guest-field__text">
        <span className="qb-guest-field__label" id={`guestField_${field}`}>
          {label}
        </span>
        <span className="qb-guest-field__state">
          {guestFieldStateText[guestFields[field]]}
        </span>
      </div>
      <div
        className="qb-segment"
        role="radiogroup"
        aria-labelledby={`guestField_${field}`}
      >
        {GUEST_FIELD_SETTINGS.map((setting) => (
          <button
            key={setting}
            type="button"
            role="radio"
            aria-checked={guestFields[field] === setting}
            className={`qb-segment__btn${
              guestFields[field] === setting ? ' qb-segment__btn--on' : ''
            }`}
            onClick={() =>
              setGuestFields((existing) => ({
                ...existing,
                [field]: setting,
              }))
            }
          >
            {guestFieldOptionLabel[setting]}
          </button>
        ))}
      </div>
    </div>
  );

  if (loading) return <Loading />;

  return (
    <div className="qb">
      <DeleteModal
        type="Question"
        visible={pickedVisible}
        handleModal={handlePickedQuestionModal}
        modalAction={handlePickedQuestionDelete}
      />
      <DeleteModal
        type="Question"
        visible={libraryVisible}
        handleModal={handleLibraryQuestionModal}
        modalAction={handleLibraryDelete}
      />
      <AlertModal
        message={qT('clearAllConfirm')}
        subText={qT('clearAllConfirmSubtext')}
        visible={clearQuestionVisible}
        handleModal={handleClearAllQuestionModal}
        modalAction={handleClearQuestions}
        modalType={AlertModalType.DELETION}
      />
      <QuestionModal
        showModal={showModal}
        editableQuestion={editingQuestion}
        onClose={handlePromptClose}
      />

      <div className="qb__header">
        <div>
          <h3 className="qb__title">{qT('pageTitle')}</h3>
          <p className="qb__subtitle">
            {tab === '1' ? qT('pageSubtitle') : qT('responsesSubtitle')}
          </p>
        </div>
        <Nav pills>
          {[
            ['1', qT('formTab')],
            ['2', qT('responsesTab')],
          ].map(([key, label]) => (
            <NavItem key={key}>
              <NavLink
                role="button"
                active={tab === key}
                onClick={() => setTab(key)}
              >
                {label}
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      </div>

      <TabContent activeTab={tab}>
        <TabPane tabId="1">
          <DragDropContext onDragEnd={handleOnDrag}>
            <div className="qb__cols">
              <div className="qb__form">
                <span className="option-group__title">
                  {qT('formCount', { count: pickedQuestions.length })}
                </span>
                <Droppable droppableId="pick">
                  {(droppableProvided, dropSnapshot) => (
                    <div
                      className={`qb__list${
                        dropSnapshot.isDraggingOver ? ' qb__list--over' : ''
                      }${
                        dropSnapshot.draggingFromThisWith
                          ? ''
                          : dropSnapshot.isDraggingOver
                            ? ' qb__list--incoming'
                            : ''
                      }`}
                      ref={droppableProvided.innerRef}
                      {...droppableProvided.droppableProps}
                    >
                      {pickedQuestions.map((q, idx) => (
                        <Draggable
                          draggableId={String(`pick-${q.id}`)}
                          index={idx}
                          key={q.id}
                        >
                          {(draggableProvided, dragSnapshot) => (
                            <div
                              className={`qb-drag${
                                dragSnapshot.isDragging
                                  ? ' qb-drag--active'
                                  : ''
                              }`}
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.dragHandleProps}
                              {...draggableProvided.draggableProps}
                            >
                              <PickedQuestionItem
                                index={idx}
                                question={q}
                                first={idx === 0}
                                last={idx === pickedQuestions.length - 1}
                                onDelete={(x) => handlePickedQuestionModal(x)}
                                editability={editability}
                                onEdit={handlePickedQuestionEdit}
                                onMoveUp={handlePickedQuestionUp}
                                onMoveDown={handlePickedQuestionDown}
                                onUpdate={handleDomainQuestionUpdate}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {droppableProvided.placeholder}
                      <div
                        className={`qb__drop-hint${
                          pickedQuestions.length ? '' : ' qb__drop-hint--empty'
                        }`}
                      >
                        <PlusIcon size={pickedQuestions.length ? 16 : 24} />
                        {pickedQuestions.length
                          ? qT('dropHint')
                          : qT('addQuestionsFromLibrary')}
                      </div>
                    </div>
                  )}
                </Droppable>

                <FormCard title={qT('guestFieldsTitle')}>
                  <p className="qb__muted">{qT('guestFieldsDescription')}</p>
                  {legacyGuests && (
                    <div className="alert alert-warning py-2 px-3">
                      {qT('legacyGuestQuestions')}
                    </div>
                  )}
                  <div className="qb__guest-fields">
                    {renderGuestFieldSelect(
                      'name',
                      qT('guestName'),
                      AccountOutlineIcon
                    )}
                    {renderGuestFieldSelect(
                      'email',
                      qT('guestEmail'),
                      EmailOutlineIcon
                    )}
                  </div>
                  {guestFields.email !== 'off' && (
                    <InfoBox icon={<LockOutlineIcon size={16} />}>
                      {qT('guestEmailPrivacy')}
                    </InfoBox>
                  )}
                </FormCard>

                {showFormButtons && (
                  <div className="qb__savebar">
                    {hasUnsavedChanges && (
                      <span className="qb__unsaved">
                        <span className="qb__unsaved-dot" />
                        {qT('unsavedChanges')}
                      </span>
                    )}
                    <div className="qb__savebar-actions">
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm mb-0"
                        disabled={!pickedQuestions.length && !hasSavedForm}
                        onClick={handleClearAllQuestionModal}
                      >
                        {qT('clearAll')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm mb-0"
                        onClick={handleAddQuestions}
                      >
                        {formButtonLabel}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <FormCard
                title={qT('library')}
                className="qb__library"
                bodyClassName="form-card__body--flush"
                actions={
                  <button
                    type="button"
                    className="btn btn-primary btn-sm mb-0 qb__new-btn"
                    onClick={addNewQuestion}
                  >
                    <PlusIcon size={14} />
                    {qT('newQuestion')}
                  </button>
                }
              >
                <div className="qb__library-tools">
                  <label className="qb__search">
                    <MagnifyIcon size={18} aria-hidden />
                    <input
                      type="search"
                      value={search}
                      onChange={handleSearch}
                      placeholder={qT('searchQuestions')}
                      aria-label={qT('searchQuestions')}
                    />
                  </label>
                  <div className="qb__filters">
                    {Object.keys(LIBRARY_FILTERS).map((key) => (
                      <button
                        key={key}
                        type="button"
                        className={`stats-range${
                          libraryFilter === key ? ' active' : ''
                        }`}
                        onClick={() => setLibraryFilter(key)}
                      >
                        {filterLabel[key]}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Questions only move from the library into the form */}
                <Droppable droppableId="list" isDropDisabled>
                  {(droppableProvided) => (
                    <div
                      className="qb__library-list"
                      ref={droppableProvided.innerRef}
                      {...droppableProvided.droppableProps}
                    >
                      {visibleLibrary.map((q, i) => (
                        <Draggable
                          draggableId={String(`list-${q.id}`)}
                          index={i}
                          key={q.id}
                          isDragDisabled={isPicked(q.id)}
                        >
                          {(draggableProvided, dragSnapshot) => (
                            <div
                              className={`qb-drag${
                                dragSnapshot.isDragging
                                  ? ' qb-drag--active'
                                  : ''
                              }`}
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.dragHandleProps}
                              {...draggableProvided.draggableProps}
                            >
                              <LibraryQuestionItem
                                question={q}
                                added={isPicked(q.id)}
                                onAdd={addFromLibrary}
                                onEdit={handleLibraryEdit}
                                onDelete={(x) => handleLibraryQuestionModal(x)}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {droppableProvided.placeholder}
                      {!visibleLibrary.length && (
                        <div className="qb__muted text-center p-4">
                          {qT('noLibraryQuestions')}
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </FormCard>
            </div>
          </DragDropContext>
        </TabPane>

        <TabPane tabId="2">
          <QuestionsStatsCard eventId={eventId} />
        </TabPane>
      </TabContent>
    </div>
  );
};
export default QuestionCard;
